import { type FC } from "react";
import { Box, Card, CardContent, CardHeader, Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import {
  useStartEntrypointPullMutation,
  useStopEntrypointPullMutation,
  type StreamEntrypoint,
} from "@/partner/entities/streamEntrypoints";
import { PullBlock } from "@/partner/shared/components/PullBlock";
import { StreamEntrypointPreview } from "./StreamEntrypointPreview";
import {
  StreamEntrypointAddresses,
  StreamEntrypointPlaybackAddresses,
} from "./StreamEntrypointAddresses";

// Совпадает с SPACING референсного StreamBroadcastTab.tsx: та же
// двухколоночная раскладка (превью+адреса слева, playback справа).
const SPACING = 2;

interface StreamEntrypointBroadcastTabProps {
  entrypoint: StreamEntrypoint;
}

// Вкладка «Трансляция»: превью и ingest-адреса точки входа, без формы и
// без своего DialogHeader (общий хедер со всеми действиями живёт в шелле
// StreamEntrypointForm.tsx, как у стримов).
export const StreamEntrypointBroadcastTab: FC<
  StreamEntrypointBroadcastTabProps
> = ({ entrypoint }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [startEntrypointPull, { loading: startingPull }] =
    useStartEntrypointPullMutation({
      onError: (error) => enqueueSnackbar(error.message, { variant: "error" }),
    });
  const [stopEntrypointPull, { loading: stoppingPull }] =
    useStopEntrypointPullMutation({
      onError: (error) => enqueueSnackbar(error.message, { variant: "error" }),
    });

  return (
    <Grid container spacing={SPACING}>
      <Grid size={{ xs: 12, sm: 6, md: 7 }}>
        <Grid container spacing={SPACING}>
          <Grid size={12}>
            <Card elevation={0}>
              <StreamEntrypointPreview entrypoint={entrypoint} />
            </Card>
          </Grid>
          <Grid size={12}>
            <Card elevation={0}>
              <CardHeader title={t("streams.form.ingestTitle")} />
              <CardContent>
                <StreamEntrypointAddresses entrypoint={entrypoint} />
                <Box sx={{ mt: 3 }}>
                  <PullBlock
                    pull={entrypoint.pull}
                    liveConnected={entrypoint.liveConnected}
                    starting={startingPull}
                    stopping={stoppingPull}
                    onStart={(sourceUrl) =>
                      void startEntrypointPull({
                        variables: { id: entrypoint.id, sourceUrl },
                      })
                    }
                    onStop={() =>
                      void stopEntrypointPull({
                        variables: { id: entrypoint.id },
                      })
                    }
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Grid>

      <Grid size={{ xs: 12, sm: 6, md: 5 }}>
        <Card elevation={0}>
          <CardHeader title={t("streams.form.playbackBlockTitle")} />
          <CardContent>
            <StreamEntrypointPlaybackAddresses entrypoint={entrypoint} />
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};
